import { AppText } from "@toothfairy/shared-ui";
import AppActivityIndicator from "@toothfairy/shared-ui/AppActivityIndicator";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppDropDown from "@toothfairy/shared-ui/AppDropDown";
import AppExpander from "@toothfairy/shared-ui/AppExpander";
import AppModal from "@toothfairy/shared-ui/AppModal";
import AppMultipleSelectInput from "@toothfairy/shared-ui/AppMultipleSelectInput";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppTable from "@toothfairy/shared-ui/AppTable";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Platform, StyleSheet, View, useWindowDimensions } from "react-native";
import ToothFairyButton from "../../Components/ToothFairyComponents/ToothFairyButton";
import IdCopyView from "../ToothFairyComponents/IdCopyView";
import useWorkspaces from "../../Hooks/useWorkspaces";
import { UserType } from "../../models";
import ToothFairyChoice from "../ToothFairyComponents/ToothFairyChoice";

const getColumns = (t, showEmail = false) => {
  const emailColumn = {
    title: t("usersInvite.columns.email"),
    width: 0.25,
  };
  if (!t) {
    const baseColumns = [
      { title: "ID", width: 0.25 },
      { title: "Name", width: 0.4 },
    ];
    const tailColumns = [
      { title: "Type", width: 0.2 },
      { title: "Actions", isAction: true },
    ];
    return showEmail
      ? [...baseColumns, { title: "Email", width: 0.25 }, ...tailColumns]
      : [...baseColumns, ...tailColumns];
  }
  const baseColumns = [
    {
      title: t("usersInvite.columns.id"),
      width: 0.25,
    },
    {
      title: t("usersInvite.columns.name"),
      width: 0.4,
    },
  ];
  const tailColumns = [
    {
      title: t("usersInvite.columns.type"),
      width: 0.2,
    },
    {
      title: t("usersInvite.columns.actions"),
      isAction: true,
    },
  ];
  return showEmail
    ? [...baseColumns, emailColumn, ...tailColumns]
    : [...baseColumns, ...tailColumns];
};

const mapTypeToName = (type, forDropdown = false, t) => {
  if (!t) {
    // Fallback when t is not available (during initialization)
    switch (type) {
      case "Root":
        return "Owner";
      case "ContentAdmin":
        return "Content Admin";
      case "AIEngineer":
        return forDropdown ? "Power User - AI Engineer" : "AI Engineer";
      case "SuperAdmin":
        return forDropdown ? "Power User - Admin" : "Admin";
      default:
        return "User";
    }
  }
  switch (type) {
    case "Root":
      return t("usersInvite.userTypes.owner");
    case "ContentAdmin":
      return t("usersInvite.userTypes.contentAdmin");
    case "AIEngineer":
      return forDropdown
        ? t("usersInvite.userTypes.aiEngineerDropdown")
        : t("usersInvite.userTypes.aiEngineer");
    case "SuperAdmin":
      return forDropdown
        ? t("usersInvite.userTypes.adminDropdown")
        : t("usersInvite.userTypes.admin");
    default:
      return t("usersInvite.userTypes.user");
  }
};

const getUserTypeCapabilities = (type, t) => {
  if (!t) {
    // Fallback when t is not available
    switch (type) {
      case UserType.SUPER_ADMIN:
        return {
          category: "Power User",
          capabilities: [
            "Full workspace administration",
            "Manage agents, prompts, and functions",
            "Access dashboard and arena",
            "Manage team members",
            "All AI Engineer capabilities",
          ],
        };
      case UserType.AI_ENGINEER:
        return {
          category: "Power User",
          capabilities: [
            "Configure agents and prompts",
            "Manage functions and connections",
            "Access documents and fine-tuning",
            "Configure providers and channels",
            "Manage benchmarks and hooks",
          ],
        };
      case UserType.CONTENT_ADMIN:
        return {
          category: "User",
          capabilities: [
            "Access Knowledge Hub",
            "Manage topics",
            "Use chat features",
            "View workspace content",
          ],
        };
      case UserType.USER:
      default:
        return {
          category: "User",
          capabilities: [
            "Access chat features",
            "Use assigned agents",
            "View allowed content",
          ],
        };
    }
  }
  switch (type) {
    case UserType.SUPER_ADMIN:
      return {
        category: t("usersInvite.capabilities.powerUser.category"),
        capabilities: t("usersInvite.capabilities.powerUser.items", {
          returnObjects: true,
        }),
      };
    case UserType.AI_ENGINEER:
      return {
        category: t("usersInvite.capabilities.aiEngineer.category"),
        capabilities: t("usersInvite.capabilities.aiEngineer.items", {
          returnObjects: true,
        }),
      };
    case UserType.CONTENT_ADMIN:
      return {
        category: t("usersInvite.capabilities.contentAdmin.category"),
        capabilities: t("usersInvite.capabilities.contentAdmin.items", {
          returnObjects: true,
        }),
      };
    case UserType.USER:
    default:
      return {
        category: t("usersInvite.capabilities.user.category"),
        capabilities: t("usersInvite.capabilities.user.items", {
          returnObjects: true,
        }),
      };
  }
};

dayjs.extend(relativeTime);
const UsersInvite = ({
  setShowModal,
  showModal,
  shareInputValue,
  shareMembers,
  handleMemberAddition,
  handleMembersInvite,
  setShareInputValue,
  handleMembersChange,
  shareMembersRef,
  isAdmin,
  forcedValidationError,
  userEmailRequestInProgress,
  handleCreation = () => {},
  usersLoading,
  setShowChoice,
  showChoice,
  handleUserDeletion = () => {},
  handleUserSelection = () => {},
  handleMemberDelete = () => {},
  selectedItem,
  setSelectedItem,
  setSelectedIndex,
  selectedIndex,
  usersAccess,
  selectedUserType,
  setSelectedUserType,
  validateSelection = () => {},
  setSelectedUserName,
  selectedUserName,
  emailValidated,
  isEditMode,
  setIsEditMode,
  changeInProgress,
  userEmailRequestFetchRequest,
  newUsers = [],
  powerUsersCount = 0,
  regularUsersCount = 0,
  canAddUserOfType = () => true,
  selectedUserIds = [],
  setSelectedUserIds = () => {},
  showBulkDeleteChoice,
  setShowBulkDeleteChoice = () => {},
  handleMembersBulkDelete = () => {},
}) => {
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();
  const { currentTheme } = AppTheme.useTheme();
  const {
    isUserAdmin,
    getActiveWorkspace,
    getMaxUsers,
    getMaxPowerUsers,
  } = useWorkspaces();
  const { width, height } = useWindowDimensions();
  const styles = generateStyles(currentTheme, width);
  const subscriptionType = getActiveWorkspace()?.subscriptionType;
  const isBusinessOrEnterprise =
    subscriptionType == "business" || subscriptionType == "enterprise";
  const maxUsers = getMaxUsers();
  const maxPowerUsers = getMaxPowerUsers();
  const isPowerUsersLimitReached = powerUsersCount >= maxPowerUsers;
  const isRegularUsersLimitReached = regularUsersCount >= maxUsers;
  const isAnyLimitReached =
    isPowerUsersLimitReached && isRegularUsersLimitReached;
  const handleInvitePress = () => {
    if (isAnyLimitReached) {
      return AppToasty.show(t("usersInvite.errors.maxUsersReached"), {
        type: "danger",
      });
    }
    handleCreation();
  };
  const colourStyles = {
    control: (styles) => ({
      ...styles,
      backgroundColor: currentTheme?.theme?.inputBackgroundColor,
      borderColor: currentTheme?.theme?.border_color,
      fontFamily: "normalFont",
      fontSize: currentTheme?.theme?.small_font_size,
      borderRadius: 10,
      color: currentTheme?.theme?.font_color,
    }),
    input: (base) => ({
      ...base,
      color: currentTheme?.theme?.font_color, // This would change the color of the text inside the input
    }),
    option: (styles, { data }) => {
      let color = currentTheme?.theme?.font_color;
      return {
        ...styles,
        backgroundColor: currentTheme?.theme?.transparent,
        color: currentTheme?.theme?.font_color,
        cursor: "pointer",

        ":active": {
          ...styles[":active"],
          backgroundColor: currentTheme?.theme?.primary,
        },
        ":hover": {
          ...styles[":hover"],
          backgroundColor: currentTheme?.theme?.primary,
        },
      };
    },
    singleValue: (styles, { data }) => ({
      ...styles,
      color: currentTheme?.theme?.font_color,
    }),
    multiValue: (styles, { data }) => {
      let color = currentTheme?.theme?.font_color;
      return {
        ...styles,
        backgroundColor: currentTheme?.theme?.primary,
      };
    },
    multiValueLabel: (styles, { data }) => ({
      ...styles,
      color: currentTheme?.theme?.font_color,
    }),
    multiValueRemove: (styles, { data }) => ({
      ...styles,
      color: currentTheme?.theme?.font_color,
      ":hover": {
        backgroundColor: currentTheme?.theme?.primary,
        color: "white",
      },
    }),
    menu: (styles) => ({
      ...styles,
      backgroundColor: currentTheme?.theme?.inputBackgroundColor,
      borderColor: currentTheme?.theme?.lightGray,
      fontFamily: "normalFont",
      fontSize: currentTheme?.theme?.small_font_size,
    }),
  };
  // from UserType generate an array with id and name using mapTypeToName mapping
  // Sorted in specific order: Power User - Admin, Power User - AI Engineer, Content Admin, User
  const userTypes = [
    {
      id: UserType.SUPER_ADMIN,
      name: mapTypeToName(UserType.SUPER_ADMIN, true, t),
    },
    {
      id: UserType.AI_ENGINEER,
      name: mapTypeToName(UserType.AI_ENGINEER, true, t),
    },
    {
      id: UserType.CONTENT_ADMIN,
      name: mapTypeToName(UserType.CONTENT_ADMIN, true, t),
    },
    { id: UserType.USER, name: mapTypeToName(UserType.USER, true, t) },
  ];
  const promiseOptions = (inputValue) => {
    return userEmailRequestFetchRequest(inputValue).then((data) => {
      if (data && data.getUserIDByEmail) {
        // Map the results to the expected format for react-select
        return data.getUserIDByEmail.map((user) => ({
          value: user.id,
          label: user.username || user.user_email,
        }));
      } else {
        return [];
      }
    });
  };

  const _coreModalForInvite = (
    <AppModal
      modalBackdrop={true}
      modalBackdropStatic={false}
      modalStyle={styles.modal}
      modalTitle={
        isEditMode
          ? t("usersInvite.modal.editTitle")
          : t("usersInvite.modal.inviteTitle")
      }
      isCloseButtonVisible
      closeModal={() => setShowModal(false)}
      isModalVisible={showModal}
      transparent={true}
      modalInnerContentStyle={{
        height: "100%",
        justifyContent: "space-between",
      }}
      modalScrollContainer={{
        height: "100%",
      }}
    >
      <View>
        {!isEditMode ? (
          <>
            <AppText
              wrapperStyle={{
                paddingTop: 20,
                paddingBottom: 10,
                marginLeft: 10,
              }}
              style={{
                fontWeight: "bold",
              }}
            >
              {t("usersInvite.modal.lookupCaption")}
            </AppText>
            <AppMultipleSelectInput
              accessibilityLabel="users_inv"
              onSelectionChange={(v) => {
                if (
                  shareMembers?.find((u) => u?.id == v?.value) ||
                  newUsers?.find((u) => u?.value == v?.value)
                ) {
                  AppToasty.show(t("usersInvite.errors.alreadyAdded"), {
                    type: "danger",
                  });
                } else {
                  handleMemberAddition(v);
                }
              }}
              selectedItems={[]}
              labelOption={"username"}
              placeholder={t("usersInvite.modal.findUsersPlaceholder")}
              isAsync={true}
              promiseOptions={promiseOptions}
              uniqueKey="id"
              showDropDowns={true}
              readOnlyHeadings={true}
              styles={colourStyles}
            />
            <AppText
              wrapperStyle={{
                paddingTop: 5,
                paddingBottom: 0,
                marginLeft: 10,
                zIndex: -1,
              }}
              style={{
                fontWeight: "bold",
                fontSize: 12,
              }}
              color={currentTheme?.theme?.orange}
            >
              {t("usersInvite.modal.mustBeRegistered")}
            </AppText>
          </>
        ) : (
          <>
            <IdCopyView
              id={selectedItem?.id}
              label={t("usersInvite.modal.userId")}
              wrapperStyle={{ marginTop: 20 }}
            />
          </>
        )}
        <View
          style={{
            zIndex: -1,
            minHeight: 300,
            justifyContent: "space-between",
          }}
        >
          <View>
            <AppText
              wrapperStyle={{
                paddingTop: 20,
                paddingBottom: 10,
                marginLeft: 10,
              }}
              style={{
                fontWeight: "bold",
              }}
            >
              {isEditMode
                ? t("usersInvite.modal.username")
                : t("usersInvite.modal.usernames")}
            </AppText>
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 5,
                flexWrap: "wrap",
              }}
            >
              {newUsers?.length > 0 ? (
                newUsers?.map((u) => {
                  return (
                    <AppText
                      wrapperStyle={{
                        paddingTop: 10,
                        paddingBottom: 0,
                        marginLeft: 10,
                      }}
                      style={{
                        color: currentTheme?.theme?.primary,
                        fontWeight: "bold",
                      }}
                    >
                      {u?.label || t("usersInvite.modal.noUserSelected")}
                    </AppText>
                  );
                })
              ) : (
                <AppText
                  wrapperStyle={{
                    paddingTop: 10,
                    paddingBottom: 0,
                    marginLeft: 10,
                  }}
                  style={{
                    color: currentTheme?.theme?.primary,
                    fontWeight: "bold",
                  }}
                >
                  {selectedItem?.label ||
                    selectedItem?.item?.label ||
                    t("usersInvite.modal.noUserSelected")}
                </AppText>
              )}
            </View>

            <AppText
              wrapperStyle={{
                paddingTop: 20,
                paddingBottom: 10,
                marginLeft: 10,
              }}
              style={{
                fontWeight: "bold",
              }}
            >
              {t("usersInvite.modal.selectAccessLevel")}
            </AppText>
            <AppDropDown
              dropDownStyle={{
                maxWidth: 250,
                marginLeft: 5,
                borderColor: currentTheme?.theme?.border_color,
              }}
              isElevated={false}
              // dropdownIconColor="transparent"
              items={userTypes}
              propertyLabel="name"
              propertyValue="id"
              selectedValue={selectedUserType}
              onValueChange={(itemValue, itemIndex) => {
                setSelectedUserType(itemValue);
              }}
            />
            {selectedUserType && (
              <AppExpander
                style={{ marginTop: 15, marginLeft: 5, zIndex: -1 }}
                expanderStyle={{ zIndex: -1 }}
                title={`${mapTypeToName(selectedUserType, true, t)} ${t(
                  "usersInvite.modal.capabilitiesSuffix",
                )}`}
                icon={AppAssets.icons.info}
                isExpandedOnLoad={true}
                iconSize={12}
                iconStyle={{ marginBottom: 2 }}
              >
                <View style={{ paddingLeft: 10, paddingBottom: 10 }}>
                  {getUserTypeCapabilities(
                    selectedUserType,
                    t,
                  )?.capabilities?.map((cap, index) => (
                    <View
                      key={`cap-${index}`}
                      style={{
                        flexDirection: "row",
                        alignItems: "flex-start",
                        marginVertical: 3,
                        paddingLeft: 5,
                      }}
                    >
                      <AppText
                        style={{
                          fontSize: 12,
                          marginRight: 8,
                        }}
                        color={currentTheme?.theme?.font_color}
                      >
                        •
                      </AppText>
                      <AppText
                        style={{
                          fontSize: 12,
                          flex: 1,
                        }}
                        color={currentTheme?.theme?.font_color}
                      >
                        {cap}
                      </AppText>
                    </View>
                  ))}
                </View>
              </AppExpander>
            )}
          </View>

          {!isEditMode && !canAddUserOfType(selectedUserType) && (
            <AppText
              wrapperStyle={{
                paddingHorizontal: 10,
                paddingBottom: 10,
              }}
              style={{
                fontSize: 12,
                textAlign: "center",
              }}
              color={currentTheme?.theme?.red}
            >
              {getUserTypeCapabilities(selectedUserType, t)?.category ===
              t("usersInvite.userTypes.powerUser")
                ? t("usersInvite.modal.limitReached.powerUser")
                : t("usersInvite.modal.limitReached.regular")}
            </AppText>
          )}
          <ToothFairyButton
            // awesomeIcon={AppAssets.icons.email}
            // {...Styles.minifiedButtonStyle}
            isDisabled={
              !emailValidated ||
              !isAdmin ||
              (!isEditMode && !canAddUserOfType(selectedUserType)) ||
              changeInProgress
            }
            wrapperStyle={{ alignSelf: "center", paddingBottom: 20 }}
            title={
              isEditMode
                ? t("usersInvite.buttons.update")
                : t("usersInvite.buttons.invite")
            }
            onPress={handleMembersInvite}
          />
        </View>
      </View>
    </AppModal>
  );

  const _coreChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("usersInvite.choice.cancelCaption")}
        caption={t("usersInvite.choice.caption")}
        subCaption={t("usersInvite.choice.subCaption")}
        confirmCaption={t("usersInvite.choice.confirmCaption")}
        onCancelButtonPress={() => setShowChoice(false)}
        onConfirmButtonPress={handleMemberDelete}
        setShowChoice={setShowChoice}
        showChoice={showChoice}
      />
    );
  }, [
    setShowChoice,
    showChoice,
    selectedItem,
    setSelectedItem,
    selectedIndex,
    handleUserDeletion,
  ]);
  const _bulkDeleteChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("usersInvite.choice.cancelCaption")}
        caption={t("usersInvite.choice.caption")}
        subCaption={t("usersInvite.choice.subCaption")}
        confirmCaption={t("usersInvite.choice.confirmCaption")}
        onCancelButtonPress={() => setShowBulkDeleteChoice(false)}
        onConfirmButtonPress={handleMembersBulkDelete}
        setShowChoice={setShowBulkDeleteChoice}
        showChoice={showBulkDeleteChoice}
      />
    );
  }, [
    setShowBulkDeleteChoice,
    showBulkDeleteChoice,
    selectedUserIds,
    handleMembersBulkDelete,
  ]);
  return (
    <View
      style={{
        minWidth: "100%",
        flex: 1,
      }}
    >
      {usersLoading ? (
        <AppActivityIndicator
          backgroundColor={currentTheme?.theme?.softBackgroundColor}
          visible={true}
          containerStyle={{
            backgroundColor: currentTheme?.theme?.transparent,
            minHeight: 250,
          }}
        />
      ) : (
        <>
          {shareMembers?.length > 0 ? (
            <AppTable
              showPagination
              itemsPerPage={25}
              isSelectable
              hideSearch={false}
              searchPlaceholder={t("usersInvite.toolbar.searchPlaceholder")}
              onSelectionChange={(selectedItems) => {
                setSelectedUserIds(selectedItems?.map((u) => u?.id) || []);
              }}
              bulkActions={[
                {
                  id: "DELETE",
                  label: t("settings.list.delete"),
                  icon: AppAssets.icons.delete,
                  isDestructive: true,
                },
              ]}
              onBulkActionPress={(id) => {
                if (id === "DELETE" && selectedUserIds?.length > 0)
                  setShowBulkDeleteChoice(true);
              }}
              marginRightBeforeActions={30}
              actionButtons
              mainActionLabel={t("common.edit")}
              showDropDownMenuForActions
              actionButtonStyle={{
                minWidth: 20,
              }}
              onClickMainAction={(item, index) => {
                if (!validateSelection(item)) return;
                setIsEditMode(true);
                handleUserSelection(item);
              }}
              onActionItemPress={async (id, item, index) => {
                if (id === "EDIT") {
                  if (!validateSelection(item)) return;
                  setIsEditMode(true);
                  handleUserSelection(item);
                } else if (id === "DELETE") {
                  if (!validateSelection(item)) return;
                  setSelectedItem(item);
                  setSelectedIndex(index);
                  setShowChoice(true);
                }
              }}
              actions={[
                {
                  label: t("common.edit"),
                  id: "EDIT",
                  icon: AppAssets.icons.edit,
                },
                {
                  label: t("common.delete"),
                  id: "DELETE",
                  icon: AppAssets.icons.delete,
                },
              ]}
              style={{
                zIndex: -1,
                height: height - 80,
                paddingRight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                marginLeft: 10,
                marginTop: 15,
                width: "auto",
              }}
              headers={getColumns(t, isUserAdmin())}
              items={shareMembers?.map((u) => {
                const extendedUser = usersAccess?.find(
                  (obj) => obj?.userID === u?.id,
                );
                return {
                  id: u?.id || "",
                  label: u?.username || t("usersInvite.placeholder.noName"),
                  ...(isUserAdmin() ? { email: u?.email || "" } : {}),
                  type: mapTypeToName(extendedUser?.userType, false, t) || "",
                  // qaUrl: u.qaUrl || "",
                  // promptPlaceholder: u.promptPlaceholder,

                  actions: " ",
                };
              })}
            >
              {isBusinessOrEnterprise && (
                <ToothFairyButton
                  title={t("usersInvite.toolbar.inviteUsers")}
                  accessibilityLabel="createButton"
                  onPress={handleInvitePress}
                />
              )}
              <AppText
                wrapperStyle={{
                  marginRight: 12,
                  paddingLeft: 10,
                }}
                style={{
                  fontWeight: "bold",
                }}
                color={
                  isPowerUsersLimitReached
                    ? currentTheme?.theme?.red
                    : currentTheme?.theme?.orange
                }
              >{`${t(
                "usersInvite.toolbar.powerUsers",
              )} ${powerUsersCount} / ${maxPowerUsers}`}</AppText>
              <AppText
                wrapperStyle={{
                  marginRight: 12,
                  paddingLeft: 10,
                }}
                style={{
                  fontWeight: "bold",
                }}
                color={
                  isRegularUsersLimitReached
                    ? currentTheme?.theme?.red
                    : currentTheme?.theme?.primary
                }
              >{`${t(
                "usersInvite.toolbar.users",
              )} ${regularUsersCount} / ${maxUsers}`}</AppText>
            </AppTable>
          ) : (
            <AppPlaceholderView
              wrapperStyle={{
                width: "50%",
                height: "50%",
                marginVertical: "auto",
                marginHorizontal: "auto",
              }}
              size={30}
              icon={AppAssets.icons.users}
              mainCaption={t("usersInvite.placeholder.noMembers")}
              subCaption={t("usersInvite.placeholder.addFirstMember")}
              actionLabel={
                isBusinessOrEnterprise
                  ? t("usersInvite.toolbar.inviteUsers")
                  : undefined
              }
              onActionPress={handleInvitePress}
            />
          )}
        </>
      )}
      {_coreModalForInvite}
      {_coreChoice}
      {_bulkDeleteChoice}
    </View>
  );
};
const generateStyles = ({ currentTheme, width }) =>
  StyleSheet.create({
    container: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
      paddingVertical: 5,
      paddingHorizontal: 15,
      height: 45,
      zIndex: 10,
      backgroundColor: currentTheme?.theme?.backgroundColor,
      shadowOpacity: 0.3,
      shadowRadius: 4.84,
      opacity: 0.9,
      shadowColor: currentTheme?.theme?.shadowColor,
      shadowOffset: {
        width: 3,
        height: 3,
      },
    },
    modal: {
      maxHeight: "95%",
      marginHorizontal: Platform.OS === "web" ? "auto" : "5%",
      marginVertical: "auto",
      width: Platform.OS === "web" && width > 500 ? "60%" : "98%",
      maxWidth: 600,
    },
    listContentContainer: {
      paddingBottom: 20,
    },
    listHeader: {
      backgroundColor: currentTheme?.theme?.lightGrey,
    },
    listItemContainer: {
      flexDirection: "row",
      padding: 10,
    },
  });

export default UsersInvite;
